<template>
  <section id="citation" class="citation-section">
    <div class="citation-inner">

      <!-- ── Citation ── -->
      <div class="section-eyebrow">
        <span class="eyebrow-dot"></span>
        <span>Cite This Work</span>
      </div>
      <h2 class="section-title">Citation</h2>
      <p class="section-desc">If you use MARVEL-40M+ in your research, please cite:</p>

      <div class="bibtex-card">
        <div class="card-glow"></div>
        <div class="bibtex-header">
          <div class="bibtex-dots">
            <span style="background:#ff5f57"></span>
            <span style="background:#febc2e"></span>
            <span style="background:#28c840"></span>
          </div>
          <span class="bibtex-filename">marvel40m.bib</span>
          <button class="copy-btn" @click="copyText" :class="{ copied: isCopied }">
            <svg v-if="!isCopied" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect width="14" height="14" x="8" y="8" rx="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
            <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>
            {{ isCopied ? 'Copied!' : 'Copy' }}
          </button>
        </div>
        <pre class="bibtex-body"><code><span class="bib-at">@inproceedings</span>{<span class="bib-key">sinha2024marvel</span>,
  <span class="bib-field">title</span>    = {<span class="bib-val">MARVEL-40M+: Multi-Level Visual Elaboration for High-Fidelity Text-to-3D Content Creation</span>},
  <span class="bib-field">author</span>   = {<span class="bib-val">Sinha, Sankalp and Khan, Mohammad Sadil and Usama, Muhammad and Sam, Shino and Stricker, Didier and Ali, Sk Aziz and Afzal, Muhammad Zeshan</span>},
  <span class="bib-field">booktitle</span> = {<span class="bib-val">CVPR</span>},
  <span class="bib-field">year</span>     = {<span class="bib-val">2025</span>},
}</code></pre>
      </div>

      <!-- ── Contact ── -->
      <div class="contact-block">
        <div class="section-eyebrow" style="margin-top: 4rem;">
          <span class="eyebrow-dot"></span>
          <span>Get In Touch</span>
        </div>
        <h2 class="section-title">Contact</h2>
        <p class="section-desc">Questions about the dataset or paper? We'd love to hear from you.</p>

        <form
          class="contact-form"
          action="https://formspree.io/f/mjvldplw"
          method="POST"
          @submit.prevent="submitForm"
        >
          <div class="form-row">
            <div class="form-group">
              <label for="cf-name">Name</label>
              <input id="cf-name" v-model="form.name" type="text" name="name" placeholder="Your name" required />
            </div>
            <div class="form-group">
              <label for="cf-email">Email</label>
              <input id="cf-email" v-model="form.email" type="email" name="email" placeholder="your@email.com" required />
            </div>
          </div>
          <div class="form-group">
            <label for="cf-message">Message</label>
            <textarea id="cf-message" v-model="form.message" name="message" rows="5" placeholder="Your message…" required></textarea>
          </div>
          <button type="submit" class="submit-btn" :class="{ sending: isSending, sent: isSent }">
            <span v-if="isSent">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M20 6 9 17l-5-5"/></svg>
              Sent!
            </span>
            <span v-else-if="isSending">Sending…</span>
            <span v-else>
              Send Message
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m22 2-7 20-4-9-9-4 20-7z"/></svg>
            </span>
          </button>
          <p class="form-error" v-if="formError">{{ formError }}</p>
        </form>
      </div>

    </div>
  </section>
</template>

<script>
export default {
  name: 'CitationSection',
  data() {
    return {
      isCopied: false,
      isSending: false,
      isSent: false,
      formError: '',
      form: { name: '', email: '', message: '' },
    };
  },
  methods: {
    copyText() {
      const text = `@inproceedings{sinha2024marvel,
  title    = {MARVEL-40M+: Multi-Level Visual Elaboration for High-Fidelity Text-to-3D Content Creation},
  author   = {Sinha, Sankalp and Khan, Mohammad Sadil and Usama, Muhammad and Sam, Shino and Stricker, Didier and Ali, Sk Aziz and Afzal, Muhammad Zeshan},
  booktitle = {CVPR},
  year     = {2025},
}`;
      navigator.clipboard.writeText(text).then(() => {
        this.isCopied = true;
        setTimeout(() => { this.isCopied = false; }, 2000);
      });
    },

    async submitForm() {
      this.isSending = true;
      this.formError = '';
      try {
        const res = await fetch('https://formspree.io/f/YOUR_FORM_ID', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify(this.form),
        });
        if (res.ok) {
          this.isSent = true;
          this.form = { name: '', email: '', message: '' };
          setTimeout(() => { this.isSent = false; }, 4000);
        } else {
          this.formError = 'Something went wrong. Please try again.';
        }
      } catch {
        this.formError = 'Network error. Please try again.';
      } finally {
        this.isSending = false;
      }
    },
  },
};
</script>

<style scoped>
.citation-section {
  padding: 6rem 2rem;
  background: linear-gradient(to bottom, transparent, rgba(91,127,255,0.04), transparent);
  border-top: 1px solid var(--border);
}
.citation-inner { max-width: 800px; margin: 0 auto; text-align: center; }

.section-eyebrow {
  display: inline-flex; align-items: center; gap: 8px;
  font-family: 'JetBrains Mono', monospace; font-size: 0.7rem;
  letter-spacing: 0.14em; text-transform: uppercase;
  color: var(--accent); margin-bottom: 1rem;
}
.eyebrow-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--accent); }
.section-title {
  font-family: 'Bebas Neue', sans-serif;
  font-size: clamp(2.5rem, 6vw, 4.5rem); letter-spacing: 0.05em;
  background: linear-gradient(135deg, var(--text), var(--accent));
  -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  margin-bottom: 0.75rem;
}
.section-desc { color: var(--text2); font-size: 0.95rem; margin-bottom: 2.5rem; }

/* BibTeX card */
.bibtex-card {
  position: relative; background: var(--surface);
  border: 1px solid var(--border2); border-radius: 20px;
  overflow: hidden; text-align: left;
  transition: border-color var(--transition), box-shadow var(--transition);
}
.bibtex-card:hover { border-color: var(--accent); box-shadow: 0 0 40px var(--glow); }
.card-glow {
  position: absolute; top: 0; left: 50%; transform: translateX(-50%);
  width: 60%; height: 1px;
  background: linear-gradient(90deg, transparent, var(--accent), transparent);
  opacity: 0.6;
}
.bibtex-header {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 18px;
  background: var(--surface2);
  border-bottom: 1px solid var(--border);
}
.bibtex-dots { display: flex; gap: 6px; }
.bibtex-dots span { width: 10px; height: 10px; border-radius: 50%; display: block; opacity: 0.7; }
.bibtex-filename {
  font-family: 'JetBrains Mono', monospace; font-size: 0.75rem;
  color: var(--text3); letter-spacing: 0.06em; flex: 1; text-align: center;
}
.copy-btn {
  display: flex; align-items: center; gap: 6px; padding: 5px 12px;
  background: var(--surface2); border: 1px solid var(--border2); border-radius: 8px;
  color: var(--text2); font-family: 'JetBrains Mono', monospace;
  font-size: 0.72rem; cursor: pointer; transition: all var(--transition);
}
.copy-btn:hover { border-color: var(--accent); color: var(--text); background: rgba(91,127,255,0.1); }
.copy-btn.copied { border-color: #28c840; color: #28c840; background: rgba(40,200,64,0.1); }
.bibtex-body {
  padding: 1.5rem 1.75rem; margin: 0; overflow-x: auto;
  font-family: 'JetBrains Mono', monospace; font-size: 0.82rem;
  line-height: 1.8; color: var(--text2); white-space: pre;
  background: var(--bg2);
}
.bibtex-body::-webkit-scrollbar { height: 3px; }
.bibtex-body::-webkit-scrollbar-thumb { background: var(--accent); }
.bib-at    { color: var(--accent2); font-weight: 600; }
.bib-key   { color: var(--accent3); }
.bib-field { color: var(--accent);  }
.bib-val   { color: var(--text);    }

/* Contact form */
.contact-block { margin-top: 1rem; }
.contact-form {
  background: var(--surface);
  border: 1px solid var(--border2);
  border-radius: 20px;
  padding: 2rem;
  text-align: left;
  transition: border-color var(--transition);
}
.contact-form:focus-within { border-color: var(--accent); }

.form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; margin-bottom: 1rem; }
.form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 1rem; }
.form-group:last-of-type { margin-bottom: 0; }

label {
  font-family: 'JetBrains Mono', monospace; font-size: 0.7rem;
  letter-spacing: 0.1em; text-transform: uppercase; color: var(--accent);
}
input, textarea {
  background: var(--bg2); border: 1px solid var(--border2);
  border-radius: 10px; padding: 11px 14px;
  color: var(--text); font-family: 'Syne', sans-serif; font-size: 0.9rem;
  outline: none; transition: border-color var(--transition), box-shadow var(--transition);
  resize: vertical;
}
input::placeholder, textarea::placeholder { color: var(--text3); }
input:focus, textarea:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px var(--glow);
}

.submit-btn {
  margin-top: 1.25rem;
  width: 100%;
  padding: 13px;
  background: rgba(91, 127, 255, 0.1);
  border: 1px solid rgba(91, 127, 255, 0.3);
  border-radius: 12px;
  color: var(--text);
  font-family: 'Syne', sans-serif;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  transition: all var(--transition);
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.submit-btn:hover {
  background: rgba(91, 127, 255, 0.2);
  border-color: rgba(91, 127, 255, 0.6);
  transform: translateY(-2px);
  box-shadow: 0 8px 32px rgba(91,127,255,0.15), inset 0 1px 0 rgba(255,255,255,0.12);
}
.submit-btn:active { transform: translateY(0); }
.submit-btn.sending {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}
.submit-btn.sent {
  background: rgba(40, 200, 64, 0.1);
  border-color: rgba(40, 200, 64, 0.4);
  box-shadow: 0 4px 20px rgba(40,200,64,0.12);
}
.form-error {
  margin-top: 0.75rem; font-size: 0.8rem;
  color: #ff6060; font-family: 'JetBrains Mono', monospace; text-align: center;
}

@media (max-width: 600px) {
  .bibtex-body { font-size: 0.7rem; padding: 1rem; }
  .form-row { grid-template-columns: 1fr; }
  .contact-form { padding: 1.25rem; }
}
</style>